// M145 — Nieuwe AppLayout met 6-item primaire nav.
// - Warm-wit canvas, wit sidebar, mint actieve staat, petrol tekst/icoon.
// - Bestaande data-testids zoveel mogelijk behouden voor backward-compat.
// - Reageert op EmbedContext: als `embedded=true`, rendert alleen children
//   (voor wrapper-pagina's die de shell zelf al bieden).
import { Link, NavLink, useNavigate, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEmbed } from "@/contexts/EmbedContext";
import { t } from "@/i18n";
import {
  ChevronDown, LogOut, LayoutDashboard, CalendarRange,
  FileStack, Share2, BarChart3, Users2, Settings,
  ShieldCheck, MessageCircleQuestion, ChevronRight,
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Logo } from "@/components/brand/Logo";

// De 6 primaire nav-items uit de referentie-design.
const NAV = [
  { to: "/", key: "nav.overzicht", label: "Overzicht", icon: LayoutDashboard, testid: "nav-overzicht" },
  { to: "/mijn-weekplan", key: "nav.mijn_weekplan", label: "Mijn weekplan", icon: CalendarRange, testid: "nav-mijn-weekplan" },
  { to: "/content", key: "nav.content", label: "Content", icon: FileStack, testid: "nav-content" },
  { to: "/kanalen", key: "nav.kanalen", label: "Kanalen", icon: Share2, testid: "nav-kanalen" },
  { to: "/resultaten", key: "nav.resultaten", label: "Resultaten", icon: BarChart3, testid: "nav-resultaten" },
  { to: "/markt-en-concurrenten", key: "nav.markt", label: "Markt & concurrenten", icon: Users2, testid: "nav-markt" },
];

const BOTTOM = [
  { to: "/instellingen", label: "Instellingen", icon: Settings, testid: "nav-instellingen" },
];

function isActiveNav(pathname, to) {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

function NavItem({ item, pathname, onClick }) {
  const Icon = item.icon;
  const active = isActiveNav(pathname, item.to);
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={item.testid}
      className={
        `flex items-center gap-3 rounded-[12px] px-4 py-3 transition-colors ${
          active
            ? "bg-accent-mint text-accent-teal"
            : "text-text-muted hover:bg-bg-canvas hover:text-text-primary"
        }`
      }
      style={{ fontSize: 15, fontWeight: 500 }}
    >
      <Icon className={`w-5 h-5 ${active ? "text-accent-teal" : ""}`} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AppLayout({ children, greetingName }) {
  const { embedded } = useEmbed();
  const { user, orgs, activeOrg, switchOrg, logout } = useAuth();
  const [orgOpen, setOrgOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  if (embedded) {
    return <>{children}</>;
  }

  const isTest = !!activeOrg?.is_test_org;
  const isDemo = activeOrg?.data_origin === "demo";

  return (
    <div className="min-h-screen bg-bg-canvas text-text-primary flex">
      {/* Skip-to-content — visueel verborgen tot toetsenbord-focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:bg-accent-teal focus:text-white focus:px-4 focus:py-2 focus:rounded-[10px] focus:shadow-lg"
        data-testid="skip-to-content"
      >
        Direct naar hoofdinhoud
      </a>

      {/* Sidebar (>=1024px) --------------------------------------------- */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-[240px] bg-bg-panel flex flex-col
                    transform transition-transform lg:transform-none
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ boxShadow: "1px 0 0 rgba(23, 44, 50, 0.05)" }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="px-6 py-5">
          <Link to="/" data-testid="brand-logo" aria-label="JustEasySEO — Overzicht">
            <Logo variant="horizontal" size={32} />
          </Link>
        </div>

        {/* Primary nav */}
        <nav
          className="flex-1 px-3 pb-3 space-y-1 overflow-y-auto"
          aria-label="Hoofdnavigatie"
        >
          {NAV.map((item) => (
            <NavItem key={item.to} item={item} pathname={loc.pathname} />
          ))}
        </nav>

        {/* Bottom nav — Instellingen */}
        <div className="mt-auto px-3 pb-4">
          <div className="border-t border-border-hairline my-2" />
          {BOTTOM.map((item) => (
            <NavItem key={item.to} item={item} pathname={loc.pathname} />
          ))}
        </div>
      </aside>

      {/* Floating action — Vraag je marketingadviseur (M145 polish) */}
      <NavLink
        to="/adviseur"
        data-testid="fab-adviseur"
        aria-label="Vraag je marketingadviseur"
        className="fixed z-40 bg-bg-panel text-text-primary rounded-[12px] inline-flex items-center gap-2 transition-all
                    bottom-6 right-6 hover:-translate-y-px hover:bg-bg-canvas
                    md:px-5 md:py-3 md:h-auto md:min-w-0
                    h-12 w-12 justify-center md:w-auto md:justify-start"
        style={{ boxShadow: "0 4px 12px rgba(23,44,50,0.08)",
                 fontSize: 14, fontWeight: 500 }}
      >
        <MessageCircleQuestion className="w-5 h-5 text-accent-teal shrink-0" aria-hidden="true" />
        <span className="hidden md:inline">Vraag je marketingadviseur</span>
      </NavLink>

      {/* Mobile burger overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      {/* Main area ------------------------------------------------------- */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 h-14 px-4 flex items-center justify-between border-b border-border-hairline bg-bg-canvas/95 backdrop-blur">
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-burger"
            aria-label="Menu openen"
            aria-expanded={mobileOpen}
            aria-controls="mobile-sidebar"
            className="p-2 -ml-2"
          >
            <div className="w-5 h-0.5 bg-text-primary mb-1" aria-hidden="true" />
            <div className="w-5 h-0.5 bg-text-primary mb-1" aria-hidden="true" />
            <div className="w-5 h-0.5 bg-text-primary" aria-hidden="true" />
          </button>
          <Link to="/" className="flex items-center gap-2" aria-label="JustEasySEO — Overzicht">
            <Logo variant="horizontal" size={22} />
          </Link>
          <div className="w-8" />
        </header>

        {/* Top bar rechts (org-switcher, user, ghost) */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-12 pt-8">
          {(isTest || isDemo) && (
            <span className="text-xs font-semibold rounded-full px-2.5 py-1"
                   style={{ background: "#F4B183", color: "#172C32" }}
                   aria-label={isDemo ? "Demo-organisatie" : "Test-organisatie"}>
              {isDemo ? "DEMO" : "TEST"}
            </span>
          )}
          <button
            onClick={() => setOrgOpen((v) => !v)}
            data-testid="org-switcher"
            aria-label="Organisatie wisselen"
            aria-expanded={orgOpen}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-btn border border-border-hairline text-sm hover:bg-bg-panel"
          >
            <span className="truncate max-w-[160px]">{activeOrg?.name || "Organisatie"}</span>
            <ChevronDown className="w-3.5 h-3.5 text-text-muted" aria-hidden="true" />
          </button>
          {orgOpen && (
            <div className="absolute right-40 top-16 z-30 min-w-[200px] rounded-md border border-border-hairline bg-bg-panel shadow-popover" role="menu">
              {(orgs || []).map((o) => (
                <button key={o.id}
                         onClick={() => { switchOrg(o.id); setOrgOpen(false); }}
                         role="menuitem"
                         className="block w-full text-left px-3 py-2 text-sm hover:bg-bg-canvas">
                  {o.name}
                </button>
              ))}
            </div>
          )}
          <div
            className="inline-flex items-center gap-2 rounded-[12px] bg-bg-panel border border-border-hairline px-3 py-1.5 shadow-card"
            role="status"
            aria-label="Ghost Mode actief. We analyseren en adviseren, we wijzigen niets."
          >
            <ShieldCheck className="w-4 h-4 text-accent-teal" aria-hidden="true" />
            <span className="text-sm text-text-primary font-medium">Ghost Mode actief</span>
          </div>
          <button
            onClick={() => setUserOpen((v) => !v)}
            data-testid="user-menu"
            aria-label="Gebruikersmenu"
            aria-expanded={userOpen}
            className="w-9 h-9 rounded-full bg-accent-mint text-accent-teal font-semibold flex items-center justify-center"
          >
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </button>
          {userOpen && (
            <div className="absolute right-4 top-16 z-30 min-w-[180px] rounded-md border border-border-hairline bg-bg-panel shadow-popover" role="menu">
              <div className="px-3 py-2 text-xs text-text-muted border-b border-border-hairline">
                {user?.email}
              </div>
              <button onClick={logout} role="menuitem" className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-bg-canvas">
                <LogOut className="w-3.5 h-3.5" aria-hidden="true" /> Uitloggen
              </button>
            </div>
          )}
        </div>

        <main id="main-content" className="max-w-[1200px] mx-auto px-6 sm:px-12 pt-6 lg:pt-10 pb-16" data-app-layout-main>
          {children}
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
